// ABOUTME: Onboarding setup page where users create and name their first workspace.
// ABOUTME: Step 2 in the Welcome -> Setup -> Personalisation onboarding flow.

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { trpc } from '@/lib/trpc'
import { Loader2 } from 'lucide-react'

/**
 * Setup Page - Step 2 of onboarding
 * User creates and names their workspace
 * Flow: Welcome → Setup → Personalisation
 */
export function SetupPage() {
    const navigate = useNavigate()
    const { user, loading: authLoading } = useAuth()
    const { workspaces, loading: workspaceLoading, refreshWorkspaces, switchWorkspace } = useWorkspace()
    
    const [workspaceName, setWorkspaceName] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [initialized, setInitialized] = useState(false)

    const createWorkspaceMutation = trpc.workspace.create.useMutation()
    const loading = createWorkspaceMutation.isPending

    // Redirect based on auth and workspace state
    useEffect(() => {
        if (authLoading || workspaceLoading) return
        
        // Redirect to welcome if not authenticated
        if (!user) {
            navigate('/app/onboarding/welcome')
            return
        }
        
        // Redirect to app if user already has workspaces
        if (workspaces.length > 0) {
            navigate('/app')
            return
        }
    }, [user, authLoading, workspaceLoading, workspaces, navigate])

    // Set default workspace name when user is loaded
    useEffect(() => {
        if (user && !initialized) {
            const userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'My'
            setWorkspaceName(`${userName}'s Workspace`)
            setInitialized(true)
        }
    }, [user, initialized])

    // Generate initials from workspace name
    const getInitials = (name: string) => {
        if (!name) return 'WS'
        const words = name.trim().split(' ')
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase()
        }
        return name.slice(0, 2).toUpperCase()
    }

    // Show loading while checking auth and workspaces
    if (authLoading || workspaceLoading) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-white dark:bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    const handleCreateWorkspace = async () => {
        if (!workspaceName.trim()) {
            setError('Please enter a workspace name')
            return
        }

        setError(null)

        try {
            // Create the workspace via tRPC mutation
            const workspace = await createWorkspaceMutation.mutateAsync({
                name: workspaceName.trim(),
            })

            // Refresh workspaces list and switch to the new workspace
            await refreshWorkspaces()
            await switchWorkspace(workspace.id)

            // Navigate to personalisation step
            navigate('/app/onboarding/personalisation')
        } catch (err) {
            console.error('Failed to create workspace:', err)
            setError(err instanceof Error ? err.message : 'Failed to create workspace')
        }
    }

    return (
        <div className="h-screen w-full flex overflow-hidden">
            {/* Left side - Form */}
            <div className="w-full lg:w-1/2 h-full flex flex-col justify-between p-8 md:p-16 lg:p-24 overflow-y-auto bg-white dark:bg-background">
                {/* Main Content */}
                <div className="max-w-md w-full mx-auto">
                    {/* Logo (Inside main container for alignment) */}
                    <div className="flex items-center gap-2 mb-8 group cursor-pointer hover:opacity-90 transition-opacity">
                        <img src="/ReflowAI Logo.svg" alt="Reflow" className="h-10 w-10 group-hover:scale-105 transition-transform" />
                        <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Reflow</span>
                    </div>

                    <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-gray-900 dark:text-white leading-tight">
                        Let's Build! <br />
                        Choose a name for your Workspace
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-12">
                        You'll be able to customize your workspace and invite your team to collaborate with you.
                    </p>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Workspace Input */}
                    <div className="flex items-center gap-4 mb-10">
                        <div className="w-16 h-16 bg-[#1F2937] text-white rounded-xl flex items-center justify-center text-xl font-medium shadow-sm shrink-0">
                            {getInitials(workspaceName)}
                        </div>
                        <div className="flex-grow">
                            <Input
                                className="w-full px-4 py-3 text-lg border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#1E1E1E] dark:text-white transition-shadow h-auto"
                                placeholder="Enter workspace name"
                                value={workspaceName}
                                onChange={(e) => setWorkspaceName(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {/* CTA Button */}
                    <Button
                        onClick={handleCreateWorkspace}
                        disabled={loading}
                        className="bg-[#18181B] hover:bg-[#27272A] text-white font-medium py-3 px-6 rounded-md uppercase text-sm tracking-wider w-fit transition-colors dark:bg-white dark:text-black dark:hover:bg-gray-200 h-auto disabled:opacity-50"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Creating...
                            </>
                        ) : (
                            'Create Workspace'
                        )}
                    </Button>
                </div>

                {/* Progress Bar */}
                <div className="mt-12 w-full max-w-md mx-auto">
                    <div className="h-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-yellow-200 to-orange-300 w-2/3 rounded-full transition-all duration-500" />
                    </div>
                </div>
            </div>

            {/* Right side - Decorative Panel */}
            <div className="hidden lg:flex w-1/2 bg-[#FFFBEB] dark:bg-[#1E1E1E] relative items-center justify-center overflow-hidden">
                {/* Background Gradients */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <div className="absolute top-[-10%] left-[10%] w-[500px] h-[500px] bg-gradient-to-br from-green-400/20 to-blue-500/20 rounded-full blur-3xl mix-blend-multiply dark:mix-blend-color-dodge" />
                    <div className="absolute top-[20%] right-[20%] w-[400px] h-[400px] bg-gradient-to-tr from-yellow-400/20 to-red-500/20 rounded-full blur-3xl mix-blend-multiply dark:mix-blend-color-dodge" />

                    {/* Abstract SVG Shapes */}
                    <svg className="absolute top-20 left-20 w-96 h-96 opacity-80" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M44.7,-76.4C58.9,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-5.3C93.5,8.6,82.2,21.5,71.2,32.4C60.2,43.3,49.5,52.2,37.8,60.2C26.1,68.2,13.4,75.3,-0.6,76.3C-14.6,77.3,-30.5,72.2,-43.3,63.6C-56.1,55,-65.8,42.9,-72.6,29.4C-79.4,15.9,-83.3,1,-80.7,-12.4C-78.1,-25.8,-69,-37.7,-58.2,-46.5C-47.4,-55.3,-34.9,-61,-22.1,-65.7C-9.3,-70.4,3.8,-74.1,17.2,-75.6C30.6,-77.1,44.2,-76.4,44.7,-76.4Z"
                            fill="#FBBF24"
                            transform="translate(100 100) scale(1.1)"
                        />
                        <path
                            className="opacity-80"
                            d="M38.1,-63.9C49.6,-59.4,59.3,-50,66.6,-39.2C73.9,-28.4,78.8,-16.2,77.5,-4.4C76.2,7.4,68.7,18.8,60.1,28.7C51.5,38.6,41.8,47,31.2,53.4C20.6,59.8,9.1,64.2,-3.5,70.3C-16.1,76.4,-29.8,84.2,-41.2,79.5C-52.6,74.8,-61.7,57.6,-66.8,41.2C-71.9,24.8,-73,9.2,-70.6,-5.3C-68.2,-19.8,-62.3,-33.2,-52.8,-43.4C-43.3,-53.6,-30.2,-60.6,-17.1,-62.7C-4,-64.8,11.3,-62,26.6,-68.4"
                            fill="#3B82F6"
                            transform="translate(40 60) scale(0.6)"
                        />
                    </svg>
                </div>

                {/* Decorative Table Card */}
                <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[90%] max-w-2xl border border-gray-100 dark:border-gray-700 z-10 overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500">
                    {/* Card Header */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-2">
                            <span className="material-icons text-gray-400 text-sm">inventory_2</span>
                            <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Competitor Product Analysis</h3>
                            <span className="text-gray-400 text-xs px-1">•••</span>
                        </div>
                        <div className="flex gap-2">
                            <button className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700">
                                <span className="material-icons text-[14px]">link</span> View sources
                            </button>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-6 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                            Default view <span className="material-icons text-[14px]">expand_more</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="material-icons text-[14px]">list</span> 124 products
                        </div>
                        <div className="flex items-center gap-1 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                            <span className="material-icons text-[14px]">filter_list</span> Filter
                        </div>
                        <div className="flex items-center gap-1 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                            <span className="material-icons text-[14px]">swap_vert</span> Sort
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 font-medium">
                                <tr>
                                    <th className="px-4 py-2 w-8">
                                        <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-black focus:ring-black" />
                                    </th>
                                    <th className="px-4 py-2 font-normal">Competitor</th>
                                    <th className="px-4 py-2 font-normal">Delivery Time</th>
                                    <th className="px-4 py-2 font-normal">Price Variance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                <TableRow
                                    index={1}
                                    company="Gorillas"
                                    companyColor="bg-purple-600"
                                    deliveryTime="~10 mins"
                                    priceVariance="-2.5% vs avg"
                                    varianceType="negative"
                                />
                                <TableRow
                                    index={2}
                                    company="Getir"
                                    companyColor="bg-blue-500"
                                    deliveryTime="~12 mins"
                                    priceVariance="+1.2% vs avg"
                                    varianceType="positive"
                                />
                                <TableRow
                                    index={3}
                                    company="Flink"
                                    companyColor="bg-pink-500"
                                    deliveryTime="~15 mins"
                                    priceVariance="0% (Parity)"
                                    varianceType="neutral"
                                />
                                <TableRow
                                    index={4}
                                    company="Zapp"
                                    companyColor="bg-teal-500"
                                    deliveryTime="~20 mins"
                                    priceVariance="-5.0% vs avg"
                                    varianceType="negative"
                                />
                                <TableRow
                                    index={5}
                                    company="Gopuff"
                                    companyColor="bg-indigo-600"
                                    deliveryTime="~15 mins"
                                    priceVariance="+3.5% vs avg"
                                    varianceType="positive"
                                />
                            </tbody>
                        </table>
                    </div>

                    {/* Loading skeleton */}
                    <div className="p-4 space-y-3 bg-white dark:bg-gray-800">
                        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded w-full" />
                        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded w-full" />
                        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded w-3/4" />
                    </div>
                </div>
            </div>
        </div>
    )
}

// Table Row Component
interface TableRowProps {
    index: number
    company: string
    companyColor: string
    deliveryTime: string
    priceVariance: string
    varianceType: 'positive' | 'negative' | 'neutral'
}

function TableRow({ index, company, companyColor, deliveryTime, priceVariance, varianceType }: TableRowProps) {
    const varianceColor = {
        positive: 'text-red-500',
        negative: 'text-emerald-500',
        neutral: 'text-gray-500'
    }[varianceType]

    const varianceIcon = {
        positive: 'arrow_upward',
        negative: 'arrow_downward',
        neutral: 'remove'
    }[varianceType]

    return (
        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <td className="px-4 py-3 text-gray-400">{index}</td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 ${companyColor} rounded-sm flex items-center justify-center text-white text-[10px] font-bold`}>
                        {company[0]}
                    </div>
                    <span className="text-gray-900 dark:text-gray-100 font-medium">{company}</span>
                </div>
            </td>
            <td className="px-4 py-3">
                <span className="text-gray-600 dark:text-gray-400">{deliveryTime}</span>
            </td>
            <td className="px-4 py-3">
                <div className={`flex items-center gap-1 font-medium ${varianceColor}`}>
                    <span className="material-icons text-[14px]">{varianceIcon}</span>
                    {priceVariance}
                </div>
            </td>
        </tr>
    )
}
